import { isPlatformBrowser, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { map } from 'rxjs/operators';
import {
  NgbNav,
  NgbNavContent,
  NgbNavItem,
  NgbNavLink,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { NewCardOfferModule } from '../../../../../shared/components/moleculas/new-card-offer/new-card.module';
import { CardPlaceholderModule } from '../../../../../shared/components/moleculas/card-placeholder/card-placeholder.module';
import { ButtonComponent } from '../../../../../shared/components';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { FLIGHT_OFFERS_CONFIG } from '../../../constants/offers.constant';
import { FlightOffersFacade } from '../../../services/flight-offers.facade';

@Component({
  selector: 'app-flight-offers',
  standalone: true,
  imports: [
    NgbNav,
    NgbNavItem,
    NgbNavLink,
    NgbNavContent,
    NewCardOfferModule,
    TitleCasePipe,
    CardPlaceholderModule,
    ButtonComponent,
    NgbNavOutlet,
    NgTemplateOutlet,
  ],
  templateUrl: './flight-offers.component.html',
  styleUrls: ['./flight-offers.component.scss', '../../../styles/sections.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightOffersComponent {
  protected readonly facade = inject(FlightOffersFacade);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly sectionOffers = viewChild<ElementRef<HTMLElement>>('sectionOffers');

  private readonly isMobile = toSignal(
    this.breakpointObserver.observe('(max-width: 768px)').pipe(map((result) => result.matches)),
    { initialValue: false }
  );

  readonly domesticLimit = signal<number>(FLIGHT_OFFERS_CONFIG.Desktop.initial);
  readonly internationalLimit = signal<number>(FLIGHT_OFFERS_CONFIG.Desktop.initial);

  readonly isLoading = this.facade.isLoading;

  readonly initialItems = computed(() =>
    this.isMobile() ? FLIGHT_OFFERS_CONFIG.Mobile.initial : FLIGHT_OFFERS_CONFIG.Desktop.initial
  );

  readonly skeletonItems = computed(() => Array.from({ length: this.domesticLimit() }));

  readonly domesticOffers = computed(() =>
    this.facade.domesticOffers().slice(0, this.domesticLimit())
  );
  readonly internationalOffers = computed(() =>
    this.facade.internationalOffers().slice(0, this.internationalLimit())
  );

  readonly isDomesticExpanded = computed(
    () => this.domesticLimit() >= this.facade.domesticOffers().length
  );
  readonly isInternationalExpanded = computed(
    () => this.internationalLimit() >= this.facade.internationalOffers().length
  );

  constructor() {
    effect(
      () => {
        const initial = this.initialItems();
        this.domesticLimit.set(initial);
        this.internationalLimit.set(initial);
      },
      { allowSignalWrites: true }
    );
  }

  protected toggleLimit(limitSignal: WritableSignal<number>, totalItems: number): void {
    const current = limitSignal();
    const initial = this.initialItems();

    if (current >= totalItems) {
      limitSignal.set(initial);
      if (this.isBrowser) setTimeout(() => this.scrollToSection(), 0);
      return;
    }

    let step: number;

    if (this.isMobile() && current === FLIGHT_OFFERS_CONFIG.Mobile.initial) {
      step = FLIGHT_OFFERS_CONFIG.Mobile.step;
    } else {
      step = this.isMobile()
        ? FLIGHT_OFFERS_CONFIG.Mobile.subsequentStep
        : FLIGHT_OFFERS_CONFIG.Desktop.step;
    }

    limitSignal.update((val) => val + step);
  }

  private scrollToSection(): void {
    const element = this.sectionOffers()?.nativeElement;
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
